import React, { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader, Badge } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { paymentRoutingService } from '@services/paymentRoutingService';
import type { PaymentRoutingDecision } from '@/types/paymentRouting';
import { formatCurrencyBRL, formatDateTimeBR } from '@pages/financial/financialUtils';
import { firstDayOfMonthISO, todayISO } from './weightedRoutingUtils';

interface RoutingDecisionsCardProps {
  companies: { company_id: number; company_name: string }[];
}

const PAGE_SIZE = 20;

const channelLabel = (id: number) => (id === 1 ? 'Site' : id === 2 ? 'CRM' : `Canal ${id}`);

const reference = (d: PaymentRoutingDecision) =>
  d.order_id ? `Pedido site #${d.order_id}` : d.comercial_proposal_id ? `Proposta #${d.comercial_proposal_id}` : '—';

/** "Últimas decisões": cada escolha de empresa feita pelo PaymentHub no modo balanceamento por peso. */
export const RoutingDecisionsCard: React.FC<RoutingDecisionsCardProps> = ({ companies }) => {
  const [from, setFrom] = useState(firstDayOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [companyId, setCompanyId] = useState(0);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PaymentRoutingDecision[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    try {
      setLoading(true);
      setError(null);
      const res = await paymentRoutingService.getDecisions({
        from, to, company_id: companyId || undefined, page, page_size: PAGE_SIZE,
      });
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar decisões');
    } finally {
      setLoading(false);
    }
  }, [from, to, companyId, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <History size={16} className="text-emerald-600" />
            Últimas decisões
          </span>
        }
        subtitle="Para qual empresa cada pagamento foi direcionado e por quê."
        action={<span className="text-sm text-slate-500">{total} registro(s)</span>}
      />
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end mb-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">De</label>
            <DateInputBR className={inputCls} value={from} onChange={v => { setFrom(v); setPage(1); }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Até</label>
            <DateInputBR className={inputCls} value={to} onChange={v => { setTo(v); setPage(1); }} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
            <select
              className={inputCls}
              value={companyId}
              onChange={e => { setCompanyId(Number(e.target.value)); setPage(1); }}
            >
              <option value={0}>Todas</option>
              {companies.map(c => (
                <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <Button variant="outline" size="sm" onClick={load} loading={loading} icon={<RefreshCw size={14} />}>
              Atualizar
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

        {items.length === 0 && !loading ? (
          <p className="text-sm text-slate-500 text-center py-6">Nenhuma decisão no período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 text-left">Quando</th>
                  <th className="px-3 py-2 text-left">Origem</th>
                  <th className="px-3 py-2 text-left">Meio</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-left">Transportadora</th>
                  <th className="px-3 py-2 text-left">Empresa</th>
                  <th className="px-3 py-2 text-left">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.payment_routing_decision_id} className="border-b border-slate-100 align-top">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTimeBR(d.created_on)}</td>
                    <td className="px-3 py-2">
                      <span className="block">{reference(d)}</span>
                      <span className="text-slate-400">{channelLabel(d.sales_channel_id)}</span>
                    </td>
                    <td className="px-3 py-2">{d.payment_kind === 'card' ? 'Cartão' : 'PIX'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrencyBRL(d.amount)}</td>
                    <td className="px-3 py-2">
                      {d.provider_name ? `${d.provider_name}${d.provider_service ? ` · ${d.provider_service}` : ''}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {d.chosen_company_name || `#${d.chosen_company_id}`}
                      {d.forced && <Badge variant="warning" className="ml-1">Forçado</Badge>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-3 text-sm">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="text-slate-600">Página {page} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>
              Próxima
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
